/**
 * Bill Runs (PRD 2026-10-21 §4, US-004): the four status columns of a row.
 *
 *   Status          System  Pending · Matched · Mismatched
 *   Status Details  System  Pending: Missing Invoice · Missing Usage Data ·
 *                           Missing Required Data · Parse Failed
 *                           Mismatched: Undercharge · Overcharge · Pending
 *   Assigned        Person  bv_bill.workflow_status (lib/billWorkflow steps)
 *   Send            Derived reportSendStatus, unchanged
 *
 * Composed from what the row already carries — billPipeline's first open
 * stage, reportBucket's verdict and reportSendStatus (all in BoardRow) — so
 * nothing here re-derives a stage, a verdict or a send state.
 */
import type { StageKey } from './billPipeline';
import type { WorkflowStep } from './billWorkflow';
import type { BillRunRow } from './billRunRows';
import type { ReportSendStatus } from './reportStatus';
import { siteValue } from './siteFilters';

export type BillRunStatusValue = 'pending' | 'matched' | 'mismatched';

export type BillRunDetail =
  | 'missing_invoice' | 'missing_usage_data' | 'missing_required_data' | 'parse_failed'
  | 'undercharge' | 'overcharge' | 'mismatch_pending';

export const BILL_RUN_STATUS_ORDER: BillRunStatusValue[] = ['pending', 'matched', 'mismatched'];

export const BILL_RUN_STATUS_LABELS: Record<BillRunStatusValue, string> = {
  pending: 'Pending',
  matched: 'Matched',
  mismatched: 'Mismatched',
};

export const BILL_RUN_DETAIL_ORDER: BillRunDetail[] = [
  'missing_invoice', 'missing_usage_data', 'missing_required_data', 'parse_failed',
  'undercharge', 'overcharge', 'mismatch_pending',
];

export const BILL_RUN_DETAIL_LABELS: Record<BillRunDetail, string> = {
  missing_invoice: 'Missing Invoice',
  missing_usage_data: 'Missing Usage Data',
  missing_required_data: 'Missing Required Data',
  parse_failed: 'Parse Failed',
  undercharge: 'Undercharge',
  overcharge: 'Overcharge',
  // Mismatched → Pending: a mismatch not yet classified as under or over (§3).
  mismatch_pending: 'Pending',
};

/**
 * Assigned Status: the stored bv_bill.workflow_status values, labelled as Bill
 * Runs shows them (PRD §4.1/§4.2). Two are renamed here only — Follow up with
 * Retailer → Query Retailer, Waiting on Customer Response → Customer Response
 * Required; lib/billWorkflow's labels (the existing pages) are unchanged.
 */
export const ASSIGNED_ORDER: WorkflowStep[] = [
  'internal_review', 'system_review', 'follow_up_retailer', 'retailer_corrected', 'waiting_customer', 'completed',
];

export const ASSIGNED_LABELS: Record<WorkflowStep, string> = {
  internal_review: 'Internal Review',
  system_review: 'System Review',
  follow_up_retailer: 'Query Retailer',
  retailer_corrected: 'Retailer Corrected',
  waiting_customer: 'Customer Response Required',
  completed: 'Completed',
};

export type BillRunStatus = {
  status: BillRunStatusValue;
  /** Null where §4.2 gives none: Pending at the bill run or report stage, Matched, and a covered or not-expected month. */
  details: BillRunDetail | null;
  /** The person-set step; Completed also when billWorkflow derived it (a within-range bill sent with no step). */
  assigned: WorkflowStep | null;
  /** reportSendStatus as BoardRow holds it; 'no_report' for a month with no bill. */
  send: ReportSendStatus;
};

const MISSING_INVOICE: StageKey[] = ['assigned', 'period', 'parsed', 'nmi'];
const REQUIRED_DATA: StageKey[] = ['network', 'tou'];

export function billRunStatus(row: BillRunRow): BillRunStatus {
  if (row.kind === 'missing') {
    return {
      status: 'pending',
      details: row.coverage === 'missing' ? 'missing_invoice' : null,
      assigned: null,
      send: 'no_report',
    };
  }

  const { pipeline, bucket, sendStatus, parseState, status: workflow, bill } = row.board;
  const assigned = bill.workflow_status ?? (workflow === 'completed' ? 'completed' : null);
  const at = (status: BillRunStatusValue, details: BillRunDetail | null): BillRunStatus =>
    ({ status, details, assigned, send: sendStatus });

  const { current } = pipeline;
  if (current !== 'done') {
    if (current === 'parsed' && parseState === 'failed') return at('pending', 'parse_failed');
    if (MISSING_INVOICE.includes(current)) return at('pending', 'missing_invoice');
    if (current === 'usage') return at('pending', 'missing_usage_data');
    if (REQUIRED_DATA.includes(current)) return at('pending', 'missing_required_data');
    return at('pending', null); // bill run not finished, or report not built
  }
  if (bucket === 'within_range') return at('matched', null);
  if (bucket === 'overcharge') return at('mismatched', 'overcharge');
  if (bucket === 'undercharge') return at('mismatched', 'undercharge');
  return at('mismatched', 'mismatch_pending');
}

/** The table's default order (US-007): missing rows, then Mismatched, Pending, Matched; then retailer, entity. */
export function compareBillRunRows(a: BillRunRow, b: BillRunRow): number {
  const rank = (r: BillRunRow) => {
    if (r.kind === 'missing') return r.coverage === 'missing' ? 0 : 4;
    const s = billRunStatus(r).status;
    return s === 'mismatched' ? 1 : s === 'pending' ? 2 : 3;
  };
  const retailer = (r: BillRunRow) =>
    r.kind === 'bill' ? siteValue(r.board.bill, 'retailer') : (r.site.current_retailer ?? '').trim();
  const entity = (r: BillRunRow) =>
    r.kind === 'bill' ? siteValue(r.board.bill, 'entity') : (r.site.client?.name ?? '').trim();
  return rank(a) - rank(b)
    || retailer(a).localeCompare(retailer(b))
    || entity(a).localeCompare(entity(b))
    || a.month.localeCompare(b.month)
    || a.key.localeCompare(b.key);
}

